/**
 * Task #555 — Engineer agent client telemetry collector.
 *
 * Privacy-first, best-effort, fire-and-forget. Captures:
 *   • errors        — window.onerror + unhandledrejection + React boundary,
 *                     with a repro breadcrumb trail, browser/env, and an
 *                     OPTIONAL best-effort screenshot + masked DOM snapshot.
 *   • performance   — navigation timing (page_load/ttfb), paint (fcp),
 *                     LCP, and long tasks via PerformanceObserver.
 *   • breadcrumbs   — TYPES only (navigation / click / api / console): action
 *                     type + route, never values, never text content.
 *
 * Hard rules: NO PII, NO input values, NO message bodies. We sanitize again on
 * the server. Everything degrades silently — telemetry must never break the app.
 */

type Crumb = { category: string; actionType: string; route: string | null; t: number };

const MAX_CRUMBS = 40;
const crumbs: Crumb[] = [];
const sessionId =
  Math.random().toString(36).slice(2) + Date.now().toString(36);

let started = false;
let lastErrorAt = 0;
const ERROR_MIN_INTERVAL_MS = 2000; // de-dupe error storms

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const LONG_NUM_RE = /\b\d{7,}\b/g;
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

function scrub(s: string, max = 4000): string {
  return s
    .replace(EMAIL_RE, "[email]")
    .replace(UUID_RE, "[id]")
    .replace(LONG_NUM_RE, "[num]")
    .slice(0, max);
}

function route(): string {
  // Path only; strip query/hash and normalize id-like segments.
  const p = window.location.pathname || "/";
  return p.replace(UUID_RE, ":id").replace(LONG_NUM_RE, ":id").slice(0, 500);
}

function envInfo() {
  const ua = navigator.userAgent || "";
  let browser = "unknown";
  if (/edg\//i.test(ua)) browser = "Edge";
  else if (/chrome\//i.test(ua)) browser = "Chrome";
  else if (/firefox\//i.test(ua)) browser = "Firefox";
  else if (/safari\//i.test(ua)) browser = "Safari";
  let os = "unknown";
  if (/windows/i.test(ua)) os = "Windows";
  else if (/mac os/i.test(ua)) os = "macOS";
  else if (/android/i.test(ua)) os = "Android";
  else if (/iphone|ipad|ios/i.test(ua)) os = "iOS";
  else if (/linux/i.test(ua)) os = "Linux";
  return {
    browser,
    os,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    appVersion: (import.meta as any).env?.VITE_APP_VERSION || "dev",
  };
}

function beacon(url: string, body: any) {
  try {
    const payload = JSON.stringify(body);
    // sendBeacon survives page unload; fall back to keepalive fetch.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* swallow */
  }
}

export function addBreadcrumb(category: string, actionType: string) {
  crumbs.push({ category, actionType: actionType.slice(0, 200), route: route(), t: Date.now() });
  if (crumbs.length > MAX_CRUMBS) crumbs.shift();
}

// ── Best-effort visual repro (fail-silent) ──────────────────────────────────
async function captureScreenshot(): Promise<string | null> {
  try {
    const mod = await import("html-to-image");
    // Cap pixel ratio + size so the upload stays small.
    const dataUrl = await mod.toPng(document.body, {
      pixelRatio: 0.5,
      cacheBust: true,
      skipFonts: true,
    });
    if (typeof dataUrl === "string" && dataUrl.length < 1_400_000) return dataUrl;
    return null;
  } catch {
    return null;
  }
}

// Masked DOM snapshot: clone the body, strip every input/textarea value and
// replace visible text nodes with length-preserving placeholders so structure
// is preserved for repro WITHOUT leaking any content.
function captureMaskedDom(): string | null {
  try {
    const clone = document.body.cloneNode(true) as HTMLElement;
    clone.querySelectorAll("script,style,svg,img,canvas,video,audio").forEach((n) => n.remove());
    clone.querySelectorAll("input,textarea,select").forEach((el) => {
      el.removeAttribute("value");
      (el as HTMLInputElement).value = "";
      el.setAttribute("data-masked", "1");
    });
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const txt = node.nodeValue || "";
        const trimmed = txt.trim();
        if (trimmed.length > 0) node.nodeValue = "•".repeat(Math.min(trimmed.length, 40));
      } else {
        node.childNodes.forEach(walk);
      }
    };
    walk(clone);
    return clone.outerHTML.slice(0, 500_000);
  } catch {
    return null;
  }
}

export async function reportError(
  message: string,
  stack: string | null,
  opts: { severity?: "error" | "warning"; withVisuals?: boolean } = {},
) {
  const now = Date.now();
  if (now - lastErrorAt < ERROR_MIN_INTERVAL_MS) return;
  lastErrorAt = now;
  try {
    const env = envInfo();
    let screenshotDataUrl: string | null = null;
    let domSnapshot: string | null = null;
    if (opts.withVisuals !== false) {
      // Visual capture is the slowest part — do it best-effort, never await-block
      // the app. Both helpers already swallow their own failures.
      screenshotDataUrl = await captureScreenshot();
      domSnapshot = captureMaskedDom();
    }
    beacon("/api/telemetry/errors", {
      source: "frontend",
      severity: opts.severity || "error",
      message: scrub(message, 4000),
      stack: stack ? scrub(stack, 8000) : null,
      route: route(),
      breadcrumbs: crumbs.slice(-40).map((c) => ({ category: c.category, actionType: c.actionType, route: c.route })),
      ...env,
      screenshotDataUrl,
      domSnapshot,
    });
  } catch {
    /* swallow */
  }
}

function flushBreadcrumbs() {
  if (crumbs.length === 0) return;
  const batch = crumbs.slice(-MAX_CRUMBS).map((c) => ({
    category: c.category,
    actionType: c.actionType,
    route: c.route,
  }));
  beacon("/api/telemetry/breadcrumbs", { sessionId, crumbs: batch });
}

function flushPerf(samples: Array<{ route: string | null; metric: string; valueMs: number }>) {
  if (samples.length === 0) return;
  beacon("/api/telemetry/perf", { samples });
}

export function initTelemetry() {
  if (started || typeof window === "undefined") return;
  started = true;

  // ── Errors ────────────────────────────────────────────────────────────────
  window.addEventListener("error", (e) => {
    const msg = e.message || "Uncaught error";
    void reportError(msg, e.error?.stack || null, { severity: "error" });
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason: any = e.reason;
    const msg = reason?.message || String(reason) || "Unhandled promise rejection";
    void reportError(msg, reason?.stack || null, { severity: "error" });
  });

  // ── Navigation + click breadcrumbs (types only) ────────────────────────────
  addBreadcrumb("navigation", "page_load");
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      // Record the element TYPE + a stable test id / role, never its text/value.
      const tag = el.tagName?.toLowerCase() || "?";
      const testId = el.getAttribute?.("data-testid");
      const label = testId ? `${tag}#${testId}` : tag;
      addBreadcrumb("click", label.slice(0, 120));
    },
    { capture: true, passive: true },
  );

  // ── Performance: navigation + paint + LCP + long tasks ─────────────────────
  try {
    const perfSamples: Array<{ route: string | null; metric: string; valueMs: number }> = [];
    // Navigation timing (page_load + ttfb) once the load event has settled.
    window.addEventListener("load", () => {
      setTimeout(() => {
        try {
          const nav = performance.getEntriesByType("navigation")[0] as
            | PerformanceNavigationTiming
            | undefined;
          if (nav) {
            perfSamples.push({ route: route(), metric: "ttfb", valueMs: nav.responseStart });
            perfSamples.push({
              route: route(),
              metric: "page_load",
              valueMs: nav.loadEventEnd || nav.domComplete,
            });
          }
          const fcp = performance.getEntriesByName("first-contentful-paint")[0];
          if (fcp) perfSamples.push({ route: route(), metric: "fcp", valueMs: fcp.startTime });
          flushPerf(perfSamples.splice(0));
        } catch {
          /* swallow */
        }
      }, 0);
    });

    if ("PerformanceObserver" in window) {
      try {
        const lcpObs = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const last = entries[entries.length - 1] as any;
          if (last) flushPerf([{ route: route(), metric: "lcp", valueMs: last.startTime }]);
        });
        lcpObs.observe({ type: "largest-contentful-paint", buffered: true } as any);
      } catch {
        /* swallow */
      }
      try {
        const longObs = new PerformanceObserver((list) => {
          const longSamples = list
            .getEntries()
            .filter((en) => en.duration >= 50)
            .map((en) => ({ route: route(), metric: "long_task", valueMs: en.duration }));
          flushPerf(longSamples);
        });
        longObs.observe({ type: "longtask", buffered: true } as any);
      } catch {
        /* swallow */
      }
    }
  } catch {
    /* swallow */
  }

  // ── Periodic + unload breadcrumb flush ─────────────────────────────────────
  setInterval(flushBreadcrumbs, 30_000);
  window.addEventListener("pagehide", flushBreadcrumbs);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushBreadcrumbs();
  });
}
